import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { SimulationAuthorization, SimulationLimits } from '@morpho-org/evm-simulation';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getSimulate, isSimulationUnsupportedSender, parseSimulatedAssetChanges, simulationAuthorizationsKey, toSimulationTx, type Transaction } from '@/services/simulate/simulate';
import { resolveSimulatedFinalPosition, selectUserTokenDelta, type SimulationStatus } from '@/services/simulate/simulatedPosition.helpers';
import { buildSimulationLimits } from '@/services/simulate/simulationLimits';
import { useVerifiedSimulationResult } from '@/services/simulate/useVerifiedSimulationResult';
import { useVaultV2 } from './useVaultV2';
import { useVaultV2Position } from './useVaultV2Position';
export type { SimulationStatus };
const BLACKLIST_ERROR_CODE = 'BLACKLIST_ERROR';
interface VaultV2SimulatedShareDeltaQueryParams {
  chainId: ChainId;
  userAddress: Address | undefined;
  vaultAddress: Address;
  simulationKey?: ReadonlyArray<unknown>;
  transactions: Transaction[] | undefined;
  authorizations?: readonly SimulationAuthorization[];
  limits: SimulationLimits | undefined;
}
function vaultV2SimulatedShareDeltaQuery({
  chainId,
  userAddress,
  vaultAddress,
  simulationKey,
  transactions,
  authorizations,
  limits
}: VaultV2SimulatedShareDeltaQueryParams) {
  return queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- The shared recap identity uses simulationKey or the final transaction.
    queryKey: ['vaultV2-simulated-share-delta', chainId, userAddress, vaultAddress, simulationKey ?? transactions?.at(-1), simulationAuthorizationsKey(authorizations)],
    queryFn: () => {
      if (!userAddress || !transactions) {
        throw new Error('Cannot simulate without a user address and transaction');
      }
      return getSimulate({
        chainId,
        transactions,
        mode: 'preview',
        authorizations,
        limits
      });
    },
    retry: false,
    staleTime: query => query.state.data?.success ? 15_000 : 0,
    gcTime: 60_000
  });
}
interface UseVaultV2SimulatedShareDeltaParams {
  /** Destination chain the bundle is built for (also the simulation chain). */
  chainId: ChainId;
  userAddress: Address | undefined;
  /** Sim only runs while the wallet is on `chainId`. */
  isWrongChain?: boolean;
  vaultAddress: Address;
  /** Builder whose *unsigned* bundle we simulate (the permit doesn't affect asset changes). */
  transaction: TransactionBuilder | undefined;
  /** Typed preview authorizations (approvals, permits, Blue authorizations) the SDK prepares before the unsigned bundle. */
  simulationAuthorizations?: readonly SimulationAuthorization[];
  /** Stable query-key identity when calldata churns each poll (e.g. wall-clock `maxSharePrice`). */
  simulationKey?: ReadonlyArray<unknown>;
  /** Inputs still building: report `loading` (skeleton) instead of flashing the JS-math estimate. */
  isPreparing?: boolean;
}

/** Single EVM sim of the unsigned bundle; returns the user's net Vault V2 share delta (verified success only) + status. */
export function useVaultV2SimulatedShareDelta(t0) {
  const $ = _c(47);
  const {
    chainId,
    userAddress,
    isWrongChain: t1,
    vaultAddress,
    transaction,
    simulationAuthorizations,
    simulationKey,
    isPreparing: t2
  } = t0;
  const isWrongChain = t1 === undefined ? false : t1;
  const isPreparing = t2 === undefined ? false : t2;
  const {
    account
  } = useWeb3Context();
  const {
    evmPreviewSimulation: isSimulationEnabled
  } = useFeatureFlags();
  let t3;
  bb0: {
    if (!transaction || !userAddress) {
      let t4;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          transactions: undefined,
          limits: undefined
        };
        $[0] = t4;
      } else {
        t4 = $[0];
      }
      t3 = t4;
      break bb0;
    }
    let finalTx;
    let t4;
    if ($[1] !== transaction || $[2] !== userAddress) {
      const builtTx = transaction.buildTx();
      finalTx = toSimulationTx(userAddress, builtTx);
      t4 = buildSimulationLimits(builtTx.action);
      $[1] = transaction;
      $[2] = userAddress;
      $[3] = finalTx;
      $[4] = t4;
    } else {
      finalTx = $[3];
      t4 = $[4];
    }
    const limits = t4;
    let t5;
    if ($[5] !== finalTx) {
      t5 = [finalTx];
      $[5] = finalTx;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== limits || $[8] !== t5) {
      t6 = {
        transactions: t5,
        limits
      };
      $[7] = limits;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t3 = t6;
  }
  const {
    transactions,
    limits: limits_0
  } = t3;
  let t4;
  if ($[10] !== account || $[11] !== chainId || $[12] !== isSimulationEnabled || $[13] !== isWrongChain || $[14] !== userAddress) {
    t4 = isSimulationEnabled && isDefined(userAddress) && !isWrongChain && !isSimulationUnsupportedSender(chainId, account);
    $[10] = account;
    $[11] = chainId;
    $[12] = isSimulationEnabled;
    $[13] = isWrongChain;
    $[14] = userAddress;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const isSimulationIntended = t4;
  let t5;
  if ($[16] !== isPreparing || $[17] !== isSimulationIntended || $[18] !== transactions) {
    t5 = isSimulationIntended && !isPreparing && isDefined(transactions);
    $[16] = isPreparing;
    $[17] = isSimulationIntended;
    $[18] = transactions;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const canFetch = t5;
  let t6;
  if ($[20] !== chainId || $[21] !== limits_0 || $[22] !== simulationAuthorizations || $[23] !== simulationKey || $[24] !== transactions || $[25] !== userAddress || $[26] !== vaultAddress) {
    t6 = vaultV2SimulatedShareDeltaQuery({
      chainId,
      userAddress,
      vaultAddress,
      simulationKey,
      transactions,
      authorizations: simulationAuthorizations,
      limits: limits_0
    });
    $[20] = chainId;
    $[21] = limits_0;
    $[22] = simulationAuthorizations;
    $[23] = simulationKey;
    $[24] = transactions;
    $[25] = userAddress;
    $[26] = vaultAddress;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== canFetch || $[29] !== t6) {
    t7 = {
      ...t6,
      enabled: canFetch,
      placeholderData: keepPreviousData
    };
    $[28] = canFetch;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const {
    data,
    error,
    isLoading: isQueryLoading,
    isFetching,
    isPlaceholderData
  } = useQuery(t7);
  const t8 = !isPreparing;
  let t9;
  if ($[31] !== data || $[32] !== error || $[33] !== t8) {
    t9 = {
      data,
      error,
      isSettled: t8
    };
    $[31] = data;
    $[32] = error;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const {
    hasVerifiedResult,
    hasSettledResult
  } = useVerifiedSimulationResult(t9);
  let t10;
  bb1: {
    if (!isSimulationIntended) {
      t10 = "disabled";
      break bb1;
    }
    if (hasVerifiedResult && hasSettledResult) {
      t10 = "success";
      break bb1;
    }
    if (isPreparing) {
      t10 = "loading";
      break bb1;
    }
    if (!isDefined(transactions)) {
      t10 = "disabled";
      break bb1;
    }
    if (isQueryLoading) {
      t10 = "loading";
      break bb1;
    }
    if (isFetching) {
      t10 = "loading";
      break bb1;
    }
    if (error) {
      t10 = "error";
      break bb1;
    }
    if (data?.code === BLACKLIST_ERROR_CODE) {
      t10 = "blacklist";
      break bb1;
    }
    t10 = "error";
  }
  const status = t10;
  const isRefreshing = isSimulationIntended && (isPreparing || isFetching || isPlaceholderData);
  let t11;
  if ($[35] !== data) {
    t11 = data?.success ? parseSimulatedAssetChanges(data.assetChanges) : undefined;
    $[35] = data;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const assetChanges = t11;
  let t12;
  if ($[37] !== assetChanges || $[38] !== userAddress || $[39] !== vaultAddress) {
    t12 = userAddress ? selectUserTokenDelta(assetChanges, userAddress, vaultAddress) : undefined;
    $[37] = assetChanges;
    $[38] = userAddress;
    $[39] = vaultAddress;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const shareDelta = t12;
  const t13 = status === "loading";
  let t14;
  if ($[41] !== assetChanges || $[42] !== isRefreshing || $[43] !== shareDelta || $[44] !== status || $[45] !== t13) {
    t14 = {
      shareDelta,
      assetChanges,
      status,
      isLoading: t13,
      isRefreshing
    };
    $[41] = assetChanges;
    $[42] = isRefreshing;
    $[43] = shareDelta;
    $[44] = status;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
}
interface UseVaultV2SimulatedFinalPositionParams {
  vaultAddress: Address;
  /** The vault's chain (keys the position read); the simulation still runs on `targetChainId`. */
  chainId: ChainId | undefined;
  transaction: TransactionBuilder | undefined;
  simulationAuthorizations?: readonly SimulationAuthorization[];
  simulationKey?: ReadonlyArray<unknown>;
  isPreparing?: boolean;
  /** JS-math position-after, shown when the simulation is unavailable/unverified. */
  fallbackPosition?: bigint;
}

/** Deposit/withdraw position-after from the user's share delta (→ `toAssets`), robust across wrap/penalties/rounding. */
export function useVaultV2SimulatedFinalPosition(t0) {
  const $ = _c(25);
  const {
    vaultAddress,
    chainId,
    transaction,
    simulationAuthorizations,
    simulationKey,
    isPreparing: t1,
    fallbackPosition
  } = t0;
  const isPreparing = t1 === undefined ? false : t1;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const {
    vaultData
  } = useVaultV2(vaultAddress);
  let t2;
  if ($[0] !== chainId || $[1] !== vaultAddress) {
    t2 = {
      vaultAddress,
      chainId
    };
    $[0] = chainId;
    $[1] = vaultAddress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    userVaultShares
  } = useVaultV2Position(t2);
  let t3;
  if ($[3] !== account.address || $[4] !== account.isWrongChain || $[5] !== isPreparing || $[6] !== simulationAuthorizations || $[7] !== simulationKey || $[8] !== targetChainId || $[9] !== transaction || $[10] !== vaultAddress) {
    t3 = {
      chainId: targetChainId,
      userAddress: account.address,
      isWrongChain: account.isWrongChain,
      vaultAddress,
      transaction,
      simulationAuthorizations,
      simulationKey,
      isPreparing
    };
    $[3] = account.address;
    $[4] = account.isWrongChain;
    $[5] = isPreparing;
    $[6] = simulationAuthorizations;
    $[7] = simulationKey;
    $[8] = targetChainId;
    $[9] = transaction;
    $[10] = vaultAddress;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    shareDelta,
    status,
    isLoading,
    isRefreshing
  } = useVaultV2SimulatedShareDelta(t3);
  let t4;
  bb0: {
    if (!isDefined(shareDelta) || !isDefined(userVaultShares) || !isDefined(vaultData)) {
      t4 = undefined;
      break bb0;
    }
    const finalShares = userVaultShares + shareDelta;
    let t5;
    if ($[12] !== finalShares || $[13] !== vaultData) {
      t5 = vaultData.toAssets(finalShares < ZERO ? ZERO : finalShares);
      $[12] = finalShares;
      $[13] = vaultData;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = t5;
  }
  const simulatedFinalPosition = t4;
  let t5;
  if ($[15] !== fallbackPosition || $[16] !== simulatedFinalPosition || $[17] !== status) {
    t5 = resolveSimulatedFinalPosition({
      status,
      simulatedValue: simulatedFinalPosition,
      fallbackPosition
    });
    $[15] = fallbackPosition;
    $[16] = simulatedFinalPosition;
    $[17] = status;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition
  } = t5;
  let t6;
  if ($[19] !== displayedFinalPosition || $[20] !== isLoading || $[21] !== isRefreshing || $[22] !== isUnverifiedFinalPosition || $[23] !== status) {
    t6 = {
      displayedFinalPosition,
      isUnverifiedFinalPosition,
      status,
      isLoading,
      isRefreshing
    };
    $[19] = displayedFinalPosition;
    $[20] = isLoading;
    $[21] = isRefreshing;
    $[22] = isUnverifiedFinalPosition;
    $[23] = status;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}